import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { BusinessService, Criticita } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from '../pages/Services'
import { Card, ErrorBanner, StatusBadge } from './common'

/**
 * Business Apps, the shared pieces: criticality choices and badge, and the
 * card on the business service page that picks the apps the service uses —
 * the service inherits the criticality of their functions. See
 * business_app_service.
 */

export const CRITICITA: Criticita[] = ['critical', 'high', 'medium', 'low']

export function CriticitaBadge({ value }: { value: Criticita | null | undefined }) {
  if (!value) return <small className="text-muted">{t('nessuna funzionalità')}</small>
  return <StatusBadge status={value} />
}

export function SceltaCriticita({ value, onChange }: { value: Criticita; onChange: (v: Criticita) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as Criticita)}>
      {CRITICITA.map((c) => <option key={c} value={c}>{CRITICALITY_LABEL[c] ?? c}</option>)}
    </select>
  )
}

export function useAmministra(): boolean {
  const { principal } = useAuth()
  const ruoli = principal?.roles ?? []
  return ruoli.includes('tenant_admin') || ruoli.includes('provider_admin')
}

/** The service page card: the Business Apps the service uses. */
export function AppDelServizio({ servizio, onChange }: { servizio: BusinessService; onChange: () => void }) {
  const admin = useAmministra()
  const tutte = useAsync(() => api.businessApps(), [])
  const [modifica, setModifica] = useState(false)
  const [scelte, setScelte] = useState<Set<string>>(new Set())
  const [errore, setErrore] = useState<unknown>(null)
  const usate = servizio.apps ?? []

  function apri() {
    setScelte(new Set(usate.map((a) => a.id)))
    setModifica(true)
  }

  async function salva() {
    setErrore(null)
    try {
      await api.setServiceApps(servizio.id, [...scelte])
      setModifica(false)
      onChange()
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <Card title={t('Business App ({n})', { n: usate.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Le applicazioni di cui il servizio è fatto. Il servizio eredita la criticità più alta fra le loro funzionalità, se è più alta di quella dichiarata, e i CI che eseguono i loro elementi diventano suoi componenti.')}
      </p>
      <ErrorBanner error={errore} />
      {!modifica && (
        usate.length === 0
          ? <p className="text-muted">{t('Nessuna Business App collegata.')}</p>
          : (
            <ul>
              {usate.map((a) => (
                <li key={a.id}><Link to={`/business-apps/${a.id}`}>{a.name}</Link> · <CriticitaBadge value={a.criticality} /></li>
              ))}
            </ul>
          )
      )}
      {modifica && (
        <div>
          {(tutte.data ?? []).length === 0 && (
            <p className="text-muted">
              {t('Non ci sono ancora Business App: si creano dalla Mappa Software o dalla pagina Business App.')}
            </p>
          )}
          {(tutte.data ?? []).map((a) => (
            <label key={a.id} className="checkbox" style={{ display: 'block' }}>
              <input type="checkbox" checked={scelte.has(a.id)} onChange={() => {
                const next = new Set(scelte)
                if (next.has(a.id)) next.delete(a.id)
                else next.add(a.id)
                setScelte(next)
              }} />{' '}
              {a.name} · <CriticitaBadge value={a.criticality} />
            </label>
          ))}
          <div className="toolbar" style={{ marginTop: 8 }}>
            <button onClick={salva}>{t('Salva')}</button>
            <button className="secondary" onClick={() => setModifica(false)}>{t('Annulla')}</button>
          </div>
        </div>
      )}
      {admin && !modifica && (
        <button className="secondary" onClick={apri}>{t('Scegli le Business App')}</button>
      )}
    </Card>
  )
}
